import { useEffect, useRef, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { notifyAdmin } from "@/lib/notifyAdmin";
import { detectBrowserLocation } from "@/lib/topCareersForEmail";


const sendWelcomeEmail = async (user: User) => {
  try {
    const email = user.email;
    if (!email) return;
    const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
    const firstName =
      (typeof meta.first_name === "string" && meta.first_name) ||
      (typeof meta.display_name === "string" && (meta.display_name as string).split(" ")[0]) ||
      undefined;
    await supabase.functions.invoke("send-transactional-email", {
      body: {
        templateName: "welcome",
        recipientEmail: email,
        idempotencyKey: `welcome-${user.id}`,
        templateData: firstName ? { firstName } : {},
      },
    });
  } catch (e) {
    // Non-fatal - don't block hydration if email send fails.
    console.warn("Failed to send welcome email", e);
  }
};

const notifyAdminSignup = (user: User, userName?: string | null) => {
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const name =
    userName ||
    (typeof meta.display_name === "string" ? (meta.display_name as string) : undefined) ||
    (typeof meta.first_name === "string" ? (meta.first_name as string) : undefined);
  const loc = detectBrowserLocation();
  const locationStr = [loc.timezone, loc.language].filter(Boolean).join(" / ") || "Unknown";
  void notifyAdmin({
    eventType: "signup",
    userName: name ?? null,
    userEmail: user.email ?? null,
    idempotencyKey: `signup-${user.id}`,
    details: {
      "Location": locationStr,
      "Referrer": loc.referrer ?? "Direct",
      "UserId": user.id,
    },
  });
};



/**
 * Bidirectional sync between localStorage assessment data and the
 * authenticated user's `assessment_results` row.
 *
 * - If localStorage has answers and the DB has fewer/none -> push local to DB.
 * - If localStorage is empty/sparse and the DB has answers -> hydrate localStorage
 *   from the DB so result pages "just work" on a fresh device.
 *
 * Exposes `hydrating` so callers can show a loading state on first sign-in
 * while we figure out which side wins.
 */
export const useAssessmentMigration = () => {
  const { user, loading: authLoading } = useAuth();
  const syncedFor = useRef<string | null>(null);
  const [hydrating, setHydrating] = useState<boolean>(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setHydrating(false);
      return;
    }
    if (syncedFor.current === user.id) return;
    syncedFor.current = user.id;

    const run = async () => {
      setHydrating(true);
      try {
        const answersRaw = localStorage.getItem("careerGPS_answers");
        const localUserName = localStorage.getItem("careerGPS_userName");

        let localAnswers: Record<string, number[]> = {};
        try {
          localAnswers = answersRaw ? JSON.parse(answersRaw) : {};
        } catch {
          localAnswers = {};
        }
        const localCount = Object.keys(localAnswers).length;

        const { data: existing } = await supabase
          .from("assessment_results")
          .select("id, answers, user_name, updated_at")
          .eq("user_id", user.id)
          .maybeSingle();

        const remoteAnswers = (existing?.answers ?? {}) as Record<string, number[]>;
        const remoteCount = Object.keys(remoteAnswers).length;

        // Case A: local has more data than remote -> push local to DB.
        if (localCount > remoteCount) {
          const payload = {
            user_id: user.id,
            user_name: localUserName,
            answers: localAnswers,
          };
          if (existing) {
            await supabase.from("assessment_results").update(payload).eq("user_id", user.id);
          } else {
            await supabase.from("assessment_results").insert(payload);
            // First-time user: send welcome email (idempotent via key).
            void sendWelcomeEmail(user);
            notifyAdminSignup(user, localUserName);
          }
        } else if (!existing) {
          // No local data either, but this is the first time we see this user -
          // still a good moment for a welcome email.
          void sendWelcomeEmail(user);
          notifyAdminSignup(user, localUserName);
        }
        // Case B: local empty/sparse but remote has data -> hydrate local.
        else if (remoteCount > 0 && remoteCount > localCount) {
          localStorage.setItem("careerGPS_answers", JSON.stringify(remoteAnswers));
          if (existing?.user_name) {
            localStorage.setItem("careerGPS_userName", existing.user_name);
          }
          // If they had completed it before (we infer from having full-ish answers),
          // mark completed so result pages don't push them back to assessment.
          localStorage.setItem("careerGPS_completed", "true");
          // Notify other components watching for assessment state changes.
          window.dispatchEvent(new Event("assessmentStateChanged"));
        }
      } finally {
        setHydrating(false);
      }
    };

    void run();
  }, [user, authLoading]);

  return { hydrating };
};
